import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Calculator, Stethoscope } from 'lucide-react';
import { Button } from '../ui/button';
import { WHATSAPP_URL } from '../../lib/utils';
import LeadForm from '../shared/LeadForm';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_hub-medico-apex/artifacts/zsn6307r_Logotipo%20com%20tag.png';
const SYMBOL_URL = 'https://customer-assets.emergentagent.com/job_hub-medico-apex/artifacts/ft6xenx5_Simbolo%20Apex.png';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/sobre', label: 'Sobre' },
  { href: '/servicos', label: 'Serviços' },
  { href: '/blog', label: 'Blog' },
  { href: '/contato', label: 'Contato' },
];

export const TOOL_LINKS = [
  { href: '/?calculadora=1', label: 'Calculadora de impostos', desc: 'Compare PF, Simples, Presumido e Equiparação', icon: Calculator, testId: 'calculadora-impostos' },
  { href: '/calculadora-plantao', label: 'Quanto vale o seu plantão', desc: 'Compare o seu plantão com o de outros cirurgiões pediátricos', icon: Stethoscope, testId: 'calculadora-plantao' },
];

const ToolsDropdown = ({ active }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 text-sm font-medium transition-colors link-underline ${active ? 'text-primary' : 'text-slate-700 hover:text-primary'}`}
        data-testid="nav-link-ferramentas"
        aria-expanded={open}
      >
        Ferramentas
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3" data-testid="nav-ferramentas-menu">
          <div className="w-[320px] rounded-xl bg-white border border-slate-200 shadow-xl p-2">
            {TOOL_LINKS.map((t) => {
              const Icon = t.icon;
              return (
                <Link key={t.href} to={t.href} onClick={() => setOpen(false)} className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`nav-tool-${t.testId}`}>
                  <span className="mt-0.5 w-8 h-8 rounded-lg bg-primary-50 text-primary flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4" /></span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{t.label}</span>
                    <span className="block text-xs text-slate-500 leading-snug">{t.desc}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleCTAClick = () => {
    setIsOpen(false);
    setShowForm(true);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'glass-nav shadow-sm' : 'bg-transparent'
        }`}
        data-testid="navbar"
      >
        <nav className="container-apex">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
              <img
                src={SYMBOL_URL}
                alt=""
                className="h-10 md:h-12 w-auto"
              />
              <img
                src={LOGO_URL}
                alt="Apex Medical Hub"
                className="h-10 md:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <React.Fragment key={link.href}>
                  {link.href === '/blog' && <ToolsDropdown active={location.pathname.startsWith('/calculadora-plantao')} />}
                  <Link
                    to={link.href}
                    className={`text-sm font-medium transition-colors link-underline ${
                      isActive(link.href)
                        ? 'text-primary'
                        : scrolled
                        ? 'text-slate-700 hover:text-primary'
                        : 'text-slate-700 hover:text-primary'
                    }`}
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                </React.Fragment>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Button
                className="btn-primary rounded-md px-6"
                data-testid="navbar-cta"
                onClick={handleCTAClick}
              >
                Falar com Especialista
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-slate-700"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Menu"
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-slate-100 shadow-lg" data-testid="mobile-menu">
              <div className="container-apex py-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`block py-3 text-base font-medium ${
                      isActive(link.href) ? 'text-primary' : 'text-slate-700'
                    }`}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-3 mt-2 border-t border-slate-100" data-testid="mobile-nav-ferramentas">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Ferramentas</p>
                  {TOOL_LINKS.map((t) => {
                    const Icon = t.icon;
                    return (
                      <Link key={t.href} to={t.href} className="flex items-center gap-3 py-3 text-base font-medium text-slate-700" data-testid={`mobile-nav-tool-${t.testId}`}>
                        <Icon className="w-5 h-5 text-primary" />
                        {t.label}
                      </Link>
                    );
                  })}
                </div>
                <div className="pt-4 mt-4 border-t border-slate-100">
                  <Button
                    className="btn-primary w-full rounded-md"
                    data-testid="mobile-navbar-cta"
                    onClick={handleCTAClick}
                  >
                    Falar com Especialista
                  </Button>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Lead capture modal */}
      {showForm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}
          data-testid="navbar-lead-modal"
        >
          <div className="relative w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setShowForm(false)}
              className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
              data-testid="navbar-modal-close"
            >
              <X className="w-4 h-4 text-slate-600" />
            </button>
            <LeadForm
              title="Fale com um especialista"
              description="Preencha seus dados e entraremos em contato. Ou siga direto para o WhatsApp após o envio."
              source="navbar-cta"
              onSuccess={() => {
                setTimeout(() => {
                  window.open(WHATSAPP_URL, '_blank');
                  setShowForm(false);
                }, 1500);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
